import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Medal } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.webp";
import { PHONE, PHONE_LINK, BUSINESS_NAME, VETERAN_DISCOUNT_TEXT } from "@/lib/constants";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <>
      {/* Veteran Announcement Banner */}
      {!bannerDismissed && (
        <div className="bg-card border-b border-primary/20 py-2 relative z-50">
          <div className="container flex items-center justify-center gap-2 text-center">
            <Medal className="h-4 w-4 text-primary flex-shrink-0" />
            <Link
              to="/contact/"
              className="text-xs sm:text-sm font-semibold text-primary hover:underline"
            >
              {VETERAN_DISCOUNT_TEXT}
            </Link>
            <button
              onClick={() => setBannerDismissed(true)}
              className="absolute right-4 text-muted-foreground hover:text-foreground p-1 lg:hidden"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container">
          <div className="flex items-center justify-between h-16 md:h-24">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src={logo}
                alt={`${BUSINESS_NAME} Logo`}
                width={624}
                height={224}
                fetchPriority="high"
                decoding="async"
                className="h-10 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                Home
              </Link>
              
              <div className="relative group">
                <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                  Services
                  <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute top-full left-0 w-64 py-2 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link
                    to="/services/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    All Plumbing Services
                  </Link>
                  <Link
                    to="/services/emergency-plumbing/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Emergency Plumbing
                  </Link>
                  <Link
                    to="/services/drain-cleaning/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Drain Cleaning
                  </Link>
                  <Link
                    to="/services/water-heater-repair-installation/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Water Heater Services
                  </Link>
                  <Link
                    to="/services/leak-detection-repair/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Leak Detection
                  </Link>
                </div>
              </div>

              <div className="relative group">
                <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors">
                  Water Purification
                  <ChevronDown className="h-4 w-4" />
                </button>
                <div className="absolute top-full left-0 w-64 py-2 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link
                    to="/water-purification/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    All Purification Services
                  </Link>
                  <Link
                    to="/water-purification/water-softeners/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Water Softeners
                  </Link>
                  <Link
                    to="/water-purification/reverse-osmosis/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Reverse Osmosis
                  </Link>
                  <Link
                    to="/water-purification/whole-home-filtration/"
                    className="block px-4 py-2 text-sm text-foreground/80 hover:text-primary hover:bg-muted transition-colors"
                  >
                    Whole Home Filtration
                  </Link>
                </div>
              </div>

              <Link
                to="/service-areas/"
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                Service Areas
              </Link>
              <Link
                to="/about/"
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                About
              </Link>
              <Link
                to="/reviews/"
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                Reviews
              </Link>
              <Link
                to="/contact/"
                className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
              >
                Contact
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <a href={PHONE_LINK}>
                <Button variant="hero" size="lg" className="gap-2">
                  <Phone className="h-4 w-4" />
                  {PHONE}
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="lg:hidden py-4 border-t border-border animate-fade-in">
              <div className="flex flex-col gap-2">
                <Link
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Home
                </Link>
                
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="flex items-center justify-between px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Services
                  <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
                
                {servicesOpen && (
                  <div className="pl-4 flex flex-col gap-1">
                    <Link
                      to="/services/"
                      onClick={() => setIsMenuOpen(false)}
                      className="px-4 py-2 text-foreground/80 hover:text-primary transition-colors"
                    >
                      All Services
                    </Link>
                    <Link
                      to="/services/emergency-plumbing/"
                      onClick={() => setIsMenuOpen(false)}
                      className="px-4 py-2 text-foreground/80 hover:text-primary transition-colors"
                    >
                      Emergency Plumbing
                    </Link>
                    <Link
                      to="/services/drain-cleaning/"
                      onClick={() => setIsMenuOpen(false)}
                      className="px-4 py-2 text-foreground/80 hover:text-primary transition-colors"
                    >
                      Drain Cleaning
                    </Link>
                  </div>
                )}

                <Link
                  to="/water-purification/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Water Purification
                </Link>
                <Link
                  to="/service-areas/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Service Areas
                </Link>
                <Link
                  to="/about/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  About
                </Link>
                <Link
                  to="/reviews/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Reviews
                </Link>
                <Link
                  to="/contact/"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  Contact
                </Link>
                
                <a href={PHONE_LINK} className="mt-2">
                  <Button variant="hero" size="lg" className="w-full gap-2">
                    <Phone className="h-4 w-4" />
                    Call {PHONE}
                  </Button>
                </a>
              </div>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
